import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import suspendModalIcon from "../../asserts/logo/SuspendModalIcon.svg";
import { ReactComponent as GreenFilledTickMarkIcon } from "../../asserts/logo/GreenFilledTickMarkIcon.svg";

const STATUS_LABEL = {
  not_reviewed: "Not reviewed",
  verified: "Verified",
  flagged: "Flagged",
  deactivated: "Deactivated",
};

const STATUS_SX = {
  not_reviewed: { width: 82, bgcolor: "#EDEDED", color: "#808080", fontWeight: 400 },
  verified: { bgcolor: "#DCFCE7", color: "#15803D", fontWeight: 600 },
  flagged: { width: 54, bgcolor: "#FFF2E5", color: "#ED7D00", fontWeight: 600 },
  deactivated: { bgcolor: "#F5F5F5", color: "#707070", fontWeight: 400 },
};

function itemDescription(tc) {
  return (tc?.name || "").trim();
}

/** Figma ModalPrompt — suspend or reactivate confirmation (single or bulk). */
export default function SuspendTestCaseDialog({
  testCase,
  testCases,
  onClose,
  onConfirm,
  submitting,
  variant = "suspend",
}) {
  const items =
    Array.isArray(testCases) && testCases.length > 0
      ? testCases
      : testCase
        ? [testCase]
        : [];
  const open = items.length > 0;
  const isBulk = items.length > 1;
  const count = items.length;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "4px",
          boxSizing: "border-box",
          width: { xs: "calc(100% - 32px)", sm: isBulk ? 600 : 480 },
          maxWidth: isBulk ? 600 : 480,
          height: isBulk ? { xs: "auto", sm: 426 } : "auto",
          maxHeight: { xs: "calc(100% - 32px)", sm: isBulk ? 426 : "none" },
          mx: 2,
          pt: "40px",
          pr: { xs: 3, sm: "64px" },
          pb: "40px",
          pl: { xs: 3, sm: "64px" },
          opacity: 1,
          transform: "rotate(0deg)",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow:
            "0px 0px 10px 0px rgba(33, 34, 38, 0.12), 0px 10px 10px 0px rgba(0, 0, 0, 0.24)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(52, 64, 84, 0.45)" },
        },
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        disabled={submitting}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "#555555",
        }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          flex: 1,
          minHeight: 0,
          width: "100%",
          overflow: "hidden",
        }}
      >
        {variant === "reactivate" ? (
          <Box
            aria-hidden
            sx={{
              position: "relative",
              width: 64,
              height: 64,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 10,
                left: 9.76,
                width: 42.68,
                height: 42.68,
                "& svg": { width: 42.68, height: 42.68, display: "block" },
                "& path": { fill: "#00754E" },
              }}
            >
              <GreenFilledTickMarkIcon />
            </Box>
          </Box>
        ) : (
          <Box
            component="img"
            src={suspendModalIcon}
            alt=""
            sx={{ width: 64, height: 64, display: "block", flexShrink: 0 }}
          />
        )}

        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 900,
            fontStyle: "normal",
            fontSize: "20px",
            lineHeight: "27px",
            letterSpacing: 0,
            color: "#000000",
            textAlign: "center",
            verticalAlign: "middle",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            flexShrink: 0,
          }}
        >
          {variant === "reactivate"
            ? isBulk
              ? `Reactivate ${count} Test Cases?`
              : "Reactivate Test Case?"
            : isBulk
              ? `Deactivate ${count} Test Cases?`
              : "Deactivate Test Case?"}
        </Typography>

        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 400,
            fontStyle: "normal",
            fontSize: "14px",
            lineHeight: "21px",
            letterSpacing: 0,
            color: "#555555",
            textAlign: "center",
            maxWidth: 420,
            mb: isBulk ? 0 : 2.5,
            flexShrink: 0,
          }}
        >
          {variant === "reactivate"
            ? isBulk
              ? "These test cases will be returned to review and included in validation runs."
              : "This test case will be returned to review and included in validation runs."
            : isBulk
              ? "These test cases will be moved to deactivated status and excluded from validation runs. You can reactivate them later."
              : "This test case will be moved to deactivated status and excluded from validation runs. You can reactivate it later."}
        </Typography>

        {isBulk ? (
          <Box
            sx={{
              boxSizing: "border-box",
              width: 472,
              maxWidth: "100%",
              opacity: 1,
              transform: "rotate(0deg)",
              borderRadius: "6px",
              pt: "12px",
              pr: "16px",
              pb: "12px",
              pl: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              bgcolor: "#F5F5F5",
              border: "1px solid #E6E6E6",
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
            }}
          >
            {items.map((tc) => (
              <Box
                key={tc.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  minWidth: 0,
                  height: 21,
                  minHeight: 21,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 700,
                    fontStyle: "normal",
                    fontSize: "13px",
                    lineHeight: "21px",
                    letterSpacing: 0,
                    color: "#333333",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {tc.name || "—"}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "13px",
                    lineHeight: "21px",
                    letterSpacing: 0,
                    color: "#808080",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {itemDescription(tc)}
                </Typography>
                <Chip
                  label={STATUS_LABEL[tc.status] || tc.status}
                  size="small"
                  sx={{
                    boxSizing: "border-box",
                    width: "auto",
                    height: 17,
                    minHeight: 17,
                    borderRadius: "10px",
                    px: "8px",
                    py: "2px",
                    opacity: 1,
                    transform: "rotate(0deg)",
                    flexShrink: 0,
                    fontFamily: "Lato, sans-serif",
                    fontStyle: "normal",
                    fontSize: "11px",
                    lineHeight: "100%",
                    letterSpacing: 0,
                    ...(STATUS_SX[tc.status] || STATUS_SX.not_reviewed),
                    "& .MuiChip-label": {
                      px: 0,
                      py: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            pt: "16px",
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontFamily: "Lato, sans-serif",
              fontWeight: 700,
              fontSize: 14,
              lineHeight: "20px",
              color: "#555555",
              borderColor: "#BBBBBB",
              borderRadius: "4px",
              px: "16px",
              height: 36,
              minHeight: 36,
              py: 0,
              flex: { xs: "1 1 120px", sm: "0 0 auto" },
              bgcolor: "#F5F5F5",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#EEEEEE",
                borderColor: "#BBBBBB",
                boxShadow: "none",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontFamily: "Lato, sans-serif",
              fontWeight: 700,
              fontSize: 14,
              lineHeight: "20px",
              color: "#FFFFFF",
              borderRadius: "4px",
              px: "16px",
              py: "8px",
              minHeight: 40,
              ...(variant === "reactivate"
                ? {
                    bgcolor: "#00754E",
                    backgroundImage:
                      "linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%)",
                    boxShadow: "0px 1px 2px 0px rgba(33, 34, 38, 0.2)",
                    "&:hover": { bgcolor: "#006040", boxShadow: "0px 1px 2px 0px rgba(33, 34, 38, 0.2)" },
                    "&.Mui-disabled": { bgcolor: "#00754E", color: "#FFFFFF", opacity: 0.6 },
                  }
                : {
                    bgcolor: "#8A5C00",
                    backgroundImage:
                      "linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%)",
                    boxShadow: "0px 1px 2px 0px rgba(33, 34, 38, 0.2)",
                    "&:hover": { bgcolor: "#6E4A00", boxShadow: "0px 1px 2px 0px rgba(33, 34, 38, 0.2)" },
                    "&.Mui-disabled": { bgcolor: "#8A5C00", color: "#FFFFFF", opacity: 0.6 },
                  }),
            }}
          >
            {submitting ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : variant === "reactivate" ? (
              "Reactivate Test case"
            ) : (
              "Deactivate Test case"
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
